<template>
    <pendo-collapse-item
        class="collapsible-nav-option nav-option expanded"
        :class="{
            selected: isParentOptionActive,
        }"
        :collapsible="option.collapsible"
        :name="option.name"
        :title="option.title"
        :suffix-icon="option.suffixIcon"
        :aria-label="option.title"
        :pendo-analytics="option.pendoAnalytics"
        :data-cy="'collapsible-nav-option-' + option.name">
        <ul class="collapsible-nav-option__children">
            <li
                v-for="child in option.children"
                :key="child.name">
                <pendo-router-link
                    class="collapsible-nav-option__child-wrapper"
                    :data-cy="`collapsible-nav-option-child-${child.name}`"
                    :pendo-analytics="child.pendoAnalytics"
                    :class="{
                        selected: isChildOptionActive(child),
                        lighten: child.overrideColor,
                        disabled: child.disabled,
                    }"
                    :disabled="child.disabled"
                    :style="isChildOptionActive(child) ? whiteLabelStyle : {}"
                    :to="child.route"
                    :aria-label="child.title"
                    :aria-current="isChildOptionActive(child) && 'page'"
                    @click="handleChildClick"
                    @keydown.enter="handleChildClick"
                    @keydown.space="handleChildClick">
                    <div
                        class="collapsible-nav-option__child"
                        data-cy="collapsible-nav-option-child">
                        <div
                            v-pendo-tooltip="{
                                content: tooltipContent,
                                arrow: true,
                                placement: 'right',
                                delay: { show: 1000, hide: 0 },
                            }"
                            class="collapsible-nav-option__child-text"
                            @mouseover="onMouseover($event, child)">
                            {{ child.title }}
                        </div>
                        <pendo-tag
                            v-if="showTag(child) && child.tag.type !== 'UpsellTag'"
                            :type="child.tag.type"
                            :size="child.tag.size || 'medium'"
                            class="collapsible-nav-option__child-tag" />
                        <upsell-tag
                            v-if="showTag(child) && child.tag.type === 'UpsellTag'"
                            :size="child.tag.size || 'medium'"
                            class="collapsible-nav-option__child-tag" />
                        <pendo-icon
                            v-if="child.customSuffixIcon"
                            v-bind="child.customSuffixIcon"
                            class="collapsible-nav-option__child-suffix-icon" />
                    </div>
                </pendo-router-link>
            </li>
        </ul>
        <template #prefix>
            <div class="nav-icon">
                <component :is="option.iconComponent" />
            </div>
        </template>
    </pendo-collapse-item>
</template>

<script>
import UpsellTag from '@/common/tags/upsell-tag.vue';
import AnalyticsNavIcon from '@/side-nav/components/nav-icons/analytics-nav-icon.vue';
import GuidesNavIcon from '@/side-nav/components/nav-icons/guides-nav-icon.vue';
import ListenNavIcon from '@/side-nav/components/nav-icons/listen-nav-icon.vue';
import NpsNavIcon from '@/side-nav/components/nav-icons/nps-nav-icon.vue';
import OrchestrateNavIcon from '@/side-nav/components/nav-icons/orchestrate-nav-icon.vue';
import PeopleNavIcon from '@/side-nav/components/nav-icons/people-nav-icon.vue';
import ProductNavIcon from '@/side-nav/components/nav-icons/product-nav-icon.vue';
import ReplayNavIcon from '@/side-nav/components/nav-icons/replay-nav-icon.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { PendoCollapseItem, PendoIcon, PendoTag, PendoTooltip } from '@pendo/components';

import { isOptionActive } from '@/side-nav/helpers/side-nav.helper';
import { elementHasOverflowX } from '@/utils/dom.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils';
import { mapGetters } from 'vuex';

export default {
    components: {
        PendoCollapseItem,
        PendoTag,
        PendoIcon,
        PendoRouterLink,
        UpsellTag,
        ProductNavIcon,
        PeopleNavIcon,
        AnalyticsNavIcon,
        ReplayNavIcon,
        ListenNavIcon,
        GuidesNavIcon,
        NpsNavIcon,
        OrchestrateNavIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        option: {
            type: Object,
            required: true,
        },
        activeOption: {
            type: Object,
            required: true,
        },
    },
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        ...mapGetters({
            isInTrial: 'subscription/isInTrial',
            trainingAttributes: 'subscription/trainingAttributes',
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
        }),
        hasPurchasedAdoptEndUser () {
            return this.hasProductLinePurchased('adoptEndUser');
        },
        whiteLabelPrimaryColor () {
            return this.hasPurchasedAdoptEndUser ? this.trainingAttributes.whiteLabelSettings?.primaryColor : null;
        },
        useWhiteLabelStyling () {
            return Boolean(this.hasPurchasedAdoptEndUser && this.whiteLabelPrimaryColor);
        },
        whiteLabelStyle () {
            if (!this.useWhiteLabelStyling) {
                return {};
            }

            return {
                '--white-label-primary-color': this.whiteLabelPrimaryColor,
            };
        },
        isParentOptionActive () {
            return isOptionActive(this.option.name, this.activeOption.topOptionName);
        },
    },
    methods: {
        isChildOptionActive (child) {
            return isOptionActive(child.name, this.activeOption.secondaryOptionName);
        },
        onMouseover ({ currentTarget }, child) {
            if (child.tooltipMessage) {
                this.tooltipContent = child.tooltipMessage;

                return;
            }

            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });
            if (hasOverflow) {
                this.tooltipContent = child.title;
            } else {
                this.tooltipContent = null;
            }
        },
        showTag (child) {
            const redirect = child.hasRedirect?.();

            return (
                (child.tag?.type === 'UpsellTag' && !this.isInTrial && redirect?.name === 'UpgradeRequired') ||
                (!!child.tag && child.tag?.type !== 'UpsellTag')
            );
        },
        handleChildClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            if ($event.type === 'keydown') {
                $event.currentTarget.click();
            }
        },
    },
};
</script>

<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.collapsible-nav-option {
    display: block !important;
    padding: 0 !important;
    opacity: unset !important;
    height: unset !important;

    :deep(.pendo-collapse-item__header) {
        height: 32px;
        line-height: unset;
        border: none;
        border-radius: 5px;
        color: var(--text-body-default-base);
        font-size: 14px;
        display: flex;
        gap: 0;
        justify-content: flex-start;
        transition: background-color ease-in 150ms;
        font-weight: 500;
        background-color: unset;
        padding: 6px 8px;
        margin-top: 2px;

        .pendo-collapse-item__title {
            margin-left: 6px;
        }

        svg {
            height: 18px;
            width: 18px;
            padding: 4px;
            box-sizing: content-box;
            display: flex;
            align-items: flex-start;
            border-radius: 5px;
        }

        &:hover {
            cursor: pointer;
            background-color: var(--surface-secondary-hover);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }

    a {
        display: block;
    }

    :deep(.pendo-collapse-item__suffix-icon) {
        margin-left: auto;

        & > svg {
            stroke: var(--text-body-subtle-base);
            height: 16px;
            width: 16px;
        }
    }

    &__child-wrapper {
        border-radius: 4px;
        transition: background-color ease-in 150ms;
        height: 30px;

        &:hover {
            cursor: pointer;
            background-color: var(--surface-secondary-hover);
        }

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }

        &.selected {
            font-weight: 600;
            background-color: var(--surface-default-background);
            border: 1px solid var(--surface-default-border);
            padding: 2px 0;
            box-shadow: 0 1px 6px var(--surface-default-drop-shadow);

            .collapsible-nav-option__child {
                margin-left: 19.5px;
                border-left: 2px solid var(--white-label-primary-color, var(--static-nav-item-active));
            }
        }

        &.lighten {
            color: var(--text-body-subtle-base);
        }

        &.disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__child {
        display: flex;
        align-items: center;
        font-weight: 500;
        padding-right: 8px;
        padding-left: 16px;
        font-size: 13.5px;
        border-left: 1px solid var(--surface-secondary-border);
        margin-left: 20px;
        height: 100%;
        gap: 4px;
    }

    &__child-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__child-tag {
        margin-left: auto;
    }

    &__child-suffix-icon {
        margin-left: auto;

        :deep(svg) {
            height: 14px;
            width: 14px;
            padding: 5px;
        }
    }

    :deep(.pendo-collapse-item__wrap) {
        background-color: unset;
    }

    &__children {
        list-style-type: none;
        padding: 0;
        margin: 0;
    }

    :deep(.pendo-collapse-item__content) {
        color: var(--text-body-default-base);
        padding: 0;
        margin: 4px 0 16px 0;
    }
}

.pendo-collapse a {
    text-decoration: none;
    color: var(--text-body-default-base);
}
</style>
